import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, IconButton, Typogaphy } from 'vnetwork-ui'
import dayjs from 'dayjs'
import BriefCaseIcons from '@/icons/BriefCaseIcons'
import FieldItem from '../shared/FieldItem'
import { Cv } from '../../../../types'
import { TYPES_OF_POSITION, DEPARTMENT } from '../../../../constants'
import { useForm } from 'react-hook-form'
import EditIcons from '@/icons/EditIcons'

interface PositionApplicationInformationProps {
    cv: any
    updateCVMutation: (values: Cv) => Promise<any>
}

const PositionApplicationInformation: React.FC<
    PositionApplicationInformationProps
> = ({ cv, updateCVMutation }) => {
    const { t } = useTranslation()
    const { t: tResume } = useTranslation('resume')
    const [open, setOpen] = useState(false)
    const form = useForm({
        defaultValues: {
            job_title: cv?.result?.job_title || '',
            cover_letter_source_name:
                cv?.result?.cover_letter_source_name || '',
            type_of_position: cv?.result?.type_of_position || '',
            department: cv?.result?.department || '',
            created_at: cv?.result?.created_at
                ? dayjs(cv?.result?.created_at).toDate()
                : null,
        },
    })

    const handleSubmit = async (values: any) => {
        await updateCVMutation({
            ...(cv?.result as Cv),
            ...values,
            created_at: values.created_at
                ? dayjs(values.created_at).toISOString()
                : cv?.result?.created_at,
        } as Cv)
        setOpen(false)
    }

    return (
        <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-3 py-2 border-b border-gray-200">
                <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-md">
                            <BriefCaseIcons className="w-4 h-4 text-white" />
                        </div>
                        <Typogaphy className="font-medium text-base text-gray-800">
                            {tResume('position-application-information')}
                        </Typogaphy>
                    </div>
                    <IconButton
                        size="small"
                        color="primary"
                        className="bg-gradient-to-br from-purple-500 to-pink-600"
                        onClick={() => setOpen(true)}
                    >
                        <EditIcons className="w-4 h-4 text-white" />
                    </IconButton>
                </div>
            </div>
            <div className="p-3 space-y-1">
                <FieldItem
                    label={t('position-applied')}
                    value={cv?.result?.job_title}
                />
                <FieldItem
                    label={t('application-date')}
                    value={
                        cv?.result?.created_at
                            ? dayjs(cv?.result?.created_at).format('DD/MM/YYYY')
                            : null
                    }
                />
                <FieldItem
                    label={t('source')}
                    value={cv?.result?.cover_letter_source_name}
                />
                <FieldItem
                    label={t('types-of-position')}
                    value={cv?.result?.type_of_position}
                />
                <FieldItem
                    label={t('department-cv')}
                    value={cv?.result?.department}
                />
            </div>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                title={t('edit')}
            >
                <form
                    className="space-y-3"
                    onSubmit={form.handleSubmit(handleSubmit)}
                >
                    <FormItem
                        control={form.control}
                        name="job_title"
                        type="textfield"
                        label={t('position-applied')}
                    />
                    <FormItem
                        control={form.control}
                        name="created_at"
                        type="datepicker"
                        label={t('application-date')}
                    />
                    <FormItem
                        control={form.control}
                        name="cover_letter_source_name"
                        type="textfield"
                        label={t('source')}
                    />
                    <FormItem
                        control={form.control}
                        name="type_of_position"
                        type="select"
                        options={TYPES_OF_POSITION}
                        label={t('types-of-position')}
                    />
                    <FormItem
                        control={form.control}
                        name="department"
                        type="select"
                        options={DEPARTMENT}
                        label={t('department-cv')}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            variant="outlined"
                            onClick={() => setOpen(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button variant="contained" type="submit">
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </Dialog>
        </div>
    )
}

export default PositionApplicationInformation
